import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface OverviewStats {
  catalogueSkus: number;
  activeBaskets: number;
  gmv7d: number;
  failedScrapes: number;
  loading: boolean;
}

export function useOverviewStats(): OverviewStats {
  const [s, setS] = useState<OverviewStats>({
    catalogueSkus: 0, activeBaskets: 0, gmv7d: 0, failedScrapes: 0, loading: true,
  });

  useEffect(() => {
    (async () => {
      const sevenDays = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const [cat, basket, gmv, fail] = await Promise.all([
        (supabase as any).from('product_catalogue').select('*', { count: 'exact', head: true }),
        (supabase as any).from('foodpreneur_profiles').select('*', { count: 'exact', head: true }).eq('onboarding_completed', true),
        (supabase as any).from('master_orders').select('total_amount').gte('created_at', sevenDays),
        (supabase as any).from('scrape_runs').select('*', { count: 'exact', head: true }).eq('status', 'failed').gte('created_at', sevenDays),
      ]);
      const sum = (gmv.data || []).reduce((a: number, r: any) => a + Number(r.total_amount || 0), 0);
      setS({
        catalogueSkus: cat.count || 0,
        activeBaskets: basket.count || 0,
        gmv7d: sum,
        failedScrapes: fail.count || 0,
        loading: false,
      });
    })();
  }, []);

  return s;
}

export interface AttentionItem {
  id: string;
  title: string;
  detail: string;
  level: 'high' | 'medium' | 'low';
  pillLabel: string;
  ts: string;
}

export function useAttentionItems() {
  const [items, setItems] = useState<AttentionItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const out: AttentionItem[] = [];
      const twoHrs = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
      const sevenDays = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

      const { data: failedRuns } = await (supabase as any)
        .from('scrape_runs')
        .select('id, status, started_at, completed_at, urls_failed')
        .eq('status', 'failed')
        .order('started_at', { ascending: false })
        .limit(5);
      (failedRuns || []).forEach((r: any) => out.push({
        id: `run-${r.id}`,
        title: 'Scrape run failed',
        detail: `${r.urls_failed || 0} URLs failed in last run`,
        level: 'high',
        pillLabel: 'Critical',
        ts: r.started_at || r.completed_at,
      }));

      const { data: stuckOrders } = await (supabase as any)
        .from('master_orders')
        .select('id, reference, status, created_at')
        .eq('status', 'pending')
        .lt('created_at', twoHrs)
        .order('created_at', { ascending: false })
        .limit(5);
      (stuckOrders || []).forEach((o: any) => out.push({
        id: `ord-${o.id}`,
        title: `Order ${o.reference} pending > 2h`,
        detail: 'Awaiting payment confirmation or fulfilment',
        level: 'medium',
        pillLabel: 'Stale',
        ts: o.created_at,
      }));

      const { data: lowConf } = await (supabase as any)
        .from('basket_item_skus')
        .select('id, basket_item_name, product_name, confidence_score, created_at')
        .lt('confidence_score', 0.7)
        .eq('verified_by_admin', false)
        .order('created_at', { ascending: false })
        .limit(5);
      (lowConf || []).forEach((m: any) => out.push({
        id: `match-${m.id}`,
        title: `Low-confidence match: ${m.basket_item_name}`,
        detail: `→ "${m.product_name}" (${Math.round(Number(m.confidence_score) * 100)}%)`,
        level: 'medium',
        pillLabel: 'Review',
        ts: m.created_at,
      }));

      const { data: readyGroups } = await (supabase as any)
        .from('buying_groups')
        .select('id, name, status, updated_at')
        .eq('status', 'open')
        .gte('updated_at', sevenDays)
        .order('updated_at', { ascending: false })
        .limit(3);
      (readyGroups || []).forEach((g: any) => out.push({
        id: `grp-${g.id}`,
        title: `Group ready: ${g.name}`,
        detail: 'Open buying group with recent activity',
        level: 'low',
        pillLabel: 'Ready',
        ts: g.updated_at,
      }));

      out.sort((a, b) => {
        const order = { high: 0, medium: 1, low: 2 };
        return order[a.level] - order[b.level] ||
          (new Date(b.ts).getTime() - new Date(a.ts).getTime());
      });
      setItems(out);
      setLoading(false);
    })();
  }, []);

  return { items, loading };
}

export function useAdminBadges() {
  const [b, setB] = useState({ scrapes: 0, catalogue: 0, orders: 0, requests: 0 });
  useEffect(() => {
    (async () => {
      const sevenDays = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const [scr, cat, ord, req] = await Promise.all([
        (supabase as any).from('scrape_runs').select('*', { count: 'exact', head: true }).eq('status', 'failed').gte('created_at', sevenDays),
        (supabase as any).from('basket_item_skus').select('*', { count: 'exact', head: true }).lt('confidence_score', 0.7).eq('verified_by_admin', false),
        (supabase as any).from('master_orders').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
        (supabase as any).from('product_requests').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
      ]);
      setB({ scrapes: scr.count || 0, catalogue: cat.count || 0, orders: ord.count || 0, requests: req.count || 0 });
    })();
  }, []);
  return b;
}
